/*
  CompareMyWatersScreen.jsx (v1.1, Water Rankings, Jul 14 2026;
  supersedes v1.0)
  Generosity, Inc. | WTR App | Water Rankings

  REPO: Generosity-WTR-APP-Free-Option-V1 (Vercel). Path:
  frontend/src/components/CompareMyWatersScreen.jsx. Complete file
  replacement. NEVER commit to the API repo.

  Full-screen overlay opened from WTRBottleScreen. Personal water
  scoreboard: every source the user has tagged, ranked by measured
  Water Score once a source holds 3 scored tests, with progress shown
  until then.

  v1.1 CHANGES FROM v1.0 (rebased on the deployed v1.0 file):
  - Retitled: the surface is now "Water Rankings". File name and
    component name stay CompareMyWatersScreen so no import site moves.
  - Top-rank callout, worded exactly "by your own tests": a provenance
    claim about the user's measurements, never a physiological claim.
    "Best for your body" is banned wording on this surface.
  - Location chips + picker: fillLocation renders as a chip on each
    row; the expanded detail offers a picker (Home / Office / Gym /
    Travel / custom) that PATCHes
    /api/bottle/compare/sources/:id/location (plusOnly).
  - Contaminant section on detail: contaminantFindings (independent
    lab results, server-ordered exceedances first, max 8) render with
    the independent_test evidence badge. Published findings about a
    brand, clearly badged, never a competing score.
  - Free-tier shaped board (middle path): the board endpoint is open;
    the SERVER sends free users the top source and a lockedCount only.
    This screen renders that honestly: the top row, N locked rows with
    no data behind them, and an upgrade note. Detail taps on the free
    tier surface the paywall message (the detail endpoint stays
    plusOnly and 403s with WTR_PLUS_REQUIRED).

  Data contract (routes/absorptionIntelligence.js v2.1):
    GET {apiBase}/api/bottle/compare/sources?email=
      { tier, board: [...], lockedCount, municipalReference,
        rankMinTests, journeyLine [, upgrade] }
    GET {apiBase}/api/bottle/compare/sources/:id?email=
      { ...source, fillLocation, tests: [...], labelData,
        contaminantFindings }
    PATCH {apiBase}/api/bottle/compare/sources/:id/location
      body { email, fillLocation } -> { ok, id, fillLocation }
  Source ids are opaque strings (Postgres BIGSERIAL serializes as
  string). Never parseInt them for identity.

  Honesty rules baked in (do not remove):
  - Evidence badges on every data block: Measured vs Label data vs
    Municipal record vs Independent test. A published number never
    renders unbadged beside a measured number.
  - Sources rank only on measured aggregates from the server. The
    client never sorts, never scores, never breaks ties.
  - No winner is pre-declared. The board says what the user's own
    tests say, nothing else. The #1 callout says "by your own tests".

  Props:
    apiBase   API base URL, same dialect as the rest of the app
    onClose   dismiss handler
    onUpgrade optional; opens the WTR+ paywall when provided
*/

import React, { useEffect, useState } from "react";
import { authHeaders } from "../lib/auth";
import { readProfile } from "../lib/profile";

const C = {
  white: "#FFFFFF",
  blue: "#51B0E6",
  blueDeep: "#2B8FC9",
  blueSoft: "#EAF5FC",
  gray: "#A6A8AB",
  light: "#F0F1F3",
  ink: "#232A33",
  green: "#2E7D5B",
  amber: "#C7A230",
  rust: "#C96B2B",
  red: "#B4433A",
  line: "rgba(15, 20, 25, 0.06)",
};

const BAND_COLOR = {
  premium: C.green,
  good: C.blue,
  moderate: C.amber,
  poor: C.rust,
  very_poor: C.red,
};

function bandColor(band) {
  return BAND_COLOR[band] || C.gray;
}

function getEmail() {
  const email = ((readProfile().email) || "").trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

function fmtDate(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/* Evidence badge. One vocabulary across the whole surface. */
function EvidenceBadge({ evidence }) {
  const map = {
    measured: { label: "Measured", color: C.blue, bg: C.blueSoft },
    label_data: { label: "Label data", color: C.gray, bg: C.light },
    municipal_record: { label: "Municipal record", color: C.gray, bg: C.light },
    independent_test: { label: "Independent test", color: C.gray, bg: C.light },
  };
  const m = map[evidence] || map.label_data;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999,
      background: m.bg, color: m.color, fontSize: 9, fontWeight: 700,
      letterSpacing: "0.08em", textTransform: "uppercase",
      fontFamily: "'Barlow Condensed', 'Montserrat', sans-serif",
    }}>
      {m.label}
    </span>
  );
}

/* Signature element: a vessel filled to the score height, meniscus on
   top. The same liquid language as the score element on the test page,
   so Compare reads as family. Pure SVG, still by design, safe under
   reduced motion. */
function ScoreVessel({ score, band, size = 72, vid }) {
  const pct = score == null ? 0 : Math.max(0, Math.min(1, score / 100));
  const w = size, h = size * 1.25;
  const inner = 6;
  const fillTop = inner + (h - inner * 2) * (1 - pct);
  const color = bandColor(band);
  const menisc = 4;
  const clipId = `vclip-${vid}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <rect x="1.5" y="1.5" width={w - 3} height={h - 3} rx={w * 0.28}
        fill={C.white} stroke={C.line} strokeWidth="1.5" />
      {pct > 0 && (
        <g>
          <clipPath id={clipId}>
            <rect x="3" y="3" width={w - 6} height={h - 6} rx={w * 0.24} />
          </clipPath>
          <g clipPath={`url(#${clipId})`}>
            <path
              d={`M 3 ${fillTop + menisc}
                  Q ${w / 2} ${fillTop - menisc} ${w - 3} ${fillTop + menisc}
                  L ${w - 3} ${h - 3} L 3 ${h - 3} Z`}
              fill={color} opacity="0.16"
            />
            <path
              d={`M 3 ${fillTop + menisc}
                  Q ${w / 2} ${fillTop - menisc} ${w - 3} ${fillTop + menisc}`}
              fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"
            />
          </g>
        </g>
      )}
      <text x={w / 2} y={h / 2 + 6} textAnchor="middle"
        fontFamily="'Montserrat', sans-serif" fontSize={size * 0.3}
        fontWeight="800" fill={score == null ? C.gray : C.ink}>
        {score == null ? "··" : Math.round(score)}
      </text>
    </svg>
  );
}

/* Ten-test sparkline, single line, no axes. */
function Sparkline({ points, band }) {
  if (!points || points.length < 2) return <div style={{ width: 64, height: 20 }} />;
  const w = 64, h = 20, pad = 2;
  const min = Math.min(...points), max = Math.max(...points);
  const span = max - min || 1;
  const step = (w - pad * 2) / (points.length - 1);
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${pad + i * step} ${pad + (h - pad * 2) * (1 - (p - min) / span)}`)
    .join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <path d={d} fill="none" stroke={bandColor(band)} strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* Progress dots toward the ranking minimum. */
function ProgressDots({ tests, needed }) {
  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      {Array.from({ length: needed }).map((_, i) => (
        <span key={i} style={{
          width: 6, height: 6, borderRadius: 999,
          background: i < tests ? C.blue : C.light,
          border: i < tests ? "none" : `1px solid ${C.gray}`,
        }} />
      ))}
    </span>
  );
}

/* v1.1: fill-location chip. Display metadata only: never participates
   in ranking, scoring, or attribution. */
function LocationChip({ location }) {
  if (!location) return null;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999,
      background: C.light, color: C.ink, fontSize: 9.5, fontWeight: 700,
      letterSpacing: "0.04em",
      fontFamily: "'Barlow Condensed', 'Montserrat', sans-serif",
    }}>
      {location}
    </span>
  );
}

const LOCATION_PRESETS = ["Home", "Office", "Gym", "Travel"];

/* v1.1: location picker, shown inside the expanded detail. Presets
   plus free text, PATCHed to the plusOnly location endpoint. Failure
   is quiet and non-destructive: the chip simply does not change. */
function LocationPicker({ apiBase, sourceId, current, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);

  async function save(value) {
    const email = getEmail();
    if (!email || saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/api/bottle/compare/sources/${sourceId}/location`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ email, fillLocation: value }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const json = await res.json();
      onSaved(json.fillLocation || null);
      setEditing(false);
      setCustom("");
    } catch (e) {
      console.error("location patch failed:", e.message);
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} style={{
        border: "none", background: "transparent", color: C.blue,
        fontSize: 10.5, fontWeight: 700, cursor: "pointer", padding: "2px 0",
        fontFamily: "inherit", letterSpacing: "0.03em",
      }}>
        {current ? `Fill location: ${current} · change` : "+ Add fill location"}
      </button>
    );
  }

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {LOCATION_PRESETS.map((p) => (
          <button key={p} onClick={() => save(p)} disabled={saving} style={{
            border: `1px solid ${current === p ? C.blue : C.line}`,
            background: current === p ? C.blueSoft : C.white,
            color: current === p ? C.blueDeep : C.ink,
            borderRadius: 999, padding: "4px 12px", fontSize: 10.5,
            fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
          }}>
            {p}
          </button>
        ))}
        {current && (
          <button onClick={() => save(null)} disabled={saving} style={{
            border: `1px solid ${C.line}`, background: C.white, color: C.gray,
            borderRadius: 999, padding: "4px 12px", fontSize: 10.5,
            fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
          }}>
            Clear
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value.slice(0, 64))}
          placeholder="Custom (e.g. Cabin, Studio)"
          style={{
            flex: 1, border: `1px solid ${C.line}`, borderRadius: 8,
            padding: "5px 8px", fontSize: 11, fontFamily: "inherit",
            color: C.ink, outline: "none",
          }}
        />
        <button
          onClick={() => custom.trim() && save(custom.trim())}
          disabled={saving || !custom.trim()}
          style={{
            border: "none", background: C.blue, color: C.white,
            borderRadius: 8, padding: "5px 12px", fontSize: 10.5,
            fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
            opacity: saving || !custom.trim() ? 0.5 : 1,
          }}>
          Save
        </button>
        <button onClick={() => { setEditing(false); setCustom(""); }} style={{
          border: "none", background: "transparent", color: C.gray,
          fontSize: 10.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
        }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* Inline source detail, lazy fetched on expand. */
function SourceDetail({ apiBase, sourceId, onLocationChange, onUpgrade }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false); // false | true | "locked"

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const email = getEmail();
      if (!email) { setErr(true); return; }
      try {
        const params = new URLSearchParams({ email });
        const res = await fetch(`${apiBase}/api/bottle/compare/sources/${sourceId}?${params.toString()}`, {
          headers: authHeaders(),
        });
        if (res.status === 403) {
          // The server refused the TIER, not the network (detail stays
          // plusOnly under the middle-path gate). Render the upgrade
          // ask, never fake data.
          let code = "";
          try { const j = await res.json(); code = j && j.code; } catch (e2) {}
          if (code === "WTR_PLUS_REQUIRED") {
            if (!cancelled) setErr("locked");
            return;
          }
          throw new Error("403");
        }
        if (!res.ok) throw new Error(String(res.status));
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        console.error("compare detail failed:", e.message);
        if (!cancelled) setErr(true);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [apiBase, sourceId]);

  if (err === "locked") {
    return (
      <div style={{ padding: "10px 0 4px", fontSize: 11.5, color: C.gray, lineHeight: 1.6 }}>
        Source detail is part of WTR+.
        {onUpgrade && (
          <button onClick={onUpgrade} style={{
            border: "none", background: "transparent", color: C.blue,
            fontSize: 11.5, fontWeight: 800, cursor: "pointer",
            fontFamily: "inherit", padding: "0 0 0 6px",
          }}>
            Upgrade
          </button>
        )}
      </div>
    );
  }
  if (err) {
    return (
      <div style={{ padding: "10px 0 4px", fontSize: 11.5, color: C.gray }}>
        Could not load this source right now. Close and reopen to try again.
      </div>
    );
  }
  if (!data) {
    return <div style={{ height: 48, background: C.light, borderRadius: 10, marginTop: 10 }} />;
  }

  return (
    <div style={{ marginTop: 10 }}>
      {/* v1.1: fill location, display metadata only */}
      <div style={{ marginBottom: 8 }}>
        <LocationPicker
          apiBase={apiBase}
          sourceId={sourceId}
          current={data.fillLocation || null}
          onSaved={(loc) => {
            setData((d) => (d ? { ...d, fillLocation: loc } : d));
            if (onLocationChange) onLocationChange(sourceId, loc);
          }}
        />
      </div>

      {/* Test history */}
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
        textTransform: "uppercase", color: C.gray, marginBottom: 6,
        fontFamily: "'Barlow Condensed', 'Montserrat', sans-serif",
      }}>
        Your tests <span style={{ marginLeft: 6 }}><EvidenceBadge evidence="measured" /></span>
      </div>
      {data.tests.length === 0 ? (
        <div style={{ fontSize: 11.5, color: C.gray }}>No scored tests yet.</div>
      ) : (
        data.tests.map((t, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "6px 0", borderBottom: i < data.tests.length - 1 ? `1px solid ${C.line}` : "none",
          }}>
            <span style={{ fontSize: 11, color: C.gray, width: 52 }}>{fmtDate(t.testedAt)}</span>
            <span style={{
              fontSize: 12.5, fontWeight: 800, width: 32, textAlign: "right",
              color: bandColor(t.scoreBand),
            }}>
              {t.score == null ? "··" : Math.round(t.score)}
            </span>
            <span style={{ fontSize: 11, color: C.ink }}>
              {t.tds == null ? "" : `TDS ${Math.round(t.tds)}`}
            </span>
            {t.attribution === "auto" && (
              <span style={{ fontSize: 9, color: C.gray, marginLeft: "auto" }}>auto-tagged</span>
            )}
          </div>
        ))
      )}

      {/* Label data block: published facts, clearly badged, never
          rendered as a competing score. */}
      {data.labelData && (
        <div style={{
          marginTop: 12, background: C.light, borderRadius: 12, padding: "10px 12px",
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
            textTransform: "uppercase", color: C.gray, marginBottom: 6,
            fontFamily: "'Barlow Condensed', 'Montserrat', sans-serif",
          }}>
            What {data.labelData.brandName} publishes <span style={{ marginLeft: 6 }}><EvidenceBadge evidence="label_data" /></span>
          </div>
          <div style={{ fontSize: 12, color: C.ink, lineHeight: 1.6 }}>
            {data.labelData.tds != null && (
              <div>{`TDS ${Math.round(data.labelData.tds)} · ${data.labelData.tdsWord}`}</div>
            )}
            {data.labelData.ph != null && <div>pH {data.labelData.ph}</div>}
            {data.labelData.sourceType && (
              <div style={{ color: C.gray, fontSize: 11 }}>
                {data.labelData.sourceType}
                {data.labelData.sourceLocation ? ` · ${data.labelData.sourceLocation}` : ""}
                {data.labelData.sourceCountry ? `, ${data.labelData.sourceCountry}` : ""}
              </div>
            )}
          </div>
          {data.tds != null && data.labelData.tds != null && (
            <div style={{ fontSize: 11, color: C.gray, marginTop: 6 }}>
              {`Your measured average: TDS ${Math.round(data.tds)}. Their label: TDS ${Math.round(data.labelData.tds)}. Trust But Verify.`}
            </div>
          )}
        </div>
      )}

      {/* v1.1: independent lab findings for the linked brand. Published
          third-party results, clearly badged, exceedances first (server
          ordering), never rendered as a competing score. A TDS meter
          cannot see these: this is what independent labs found. */}
      {data.contaminantFindings && data.contaminantFindings.length > 0 && (
        <div style={{
          marginTop: 12, background: C.light, borderRadius: 12, padding: "10px 12px",
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
            textTransform: "uppercase", color: C.gray, marginBottom: 6,
            fontFamily: "'Barlow Condensed', 'Montserrat', sans-serif",
          }}>
            What independent labs found <span style={{ marginLeft: 6 }}><EvidenceBadge evidence="independent_test" /></span>
          </div>
          {data.contaminantFindings.map((f, i) => {
            const exceeds = f.exceedsFda || f.exceedsEwg;
            return (
              <div key={i} style={{
                padding: "6px 0",
                borderBottom: i < data.contaminantFindings.length - 1 ? `1px solid ${C.line}` : "none",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: exceeds ? C.rust : C.ink }}>
                    {f.name}
                  </span>
                  {exceeds && (
                    <span style={{
                      fontSize: 9, fontWeight: 800, color: C.rust,
                      letterSpacing: "0.06em", textTransform: "uppercase",
                    }}>
                      {f.exceedsFda ? "Above FDA limit" : "Above EWG guideline"}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 10.5, color: C.gray, marginTop: 2, lineHeight: 1.5 }}>
                  {f.concentration != null ? `${f.concentration}${f.unit ? ` ${f.unit}` : ""}` : ""}
                  {f.testOrg ? `${f.concentration != null ? " · " : ""}${f.testOrg}` : ""}
                  {f.testYear ? ` (${f.testYear})` : ""}
                </div>
                {f.summary && (
                  <div style={{ fontSize: 10.5, color: C.ink, marginTop: 2, lineHeight: 1.5 }}>
                    {f.summary}
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ fontSize: 10, color: C.gray, marginTop: 8, lineHeight: 1.5 }}>
            Published independent lab results for this brand. Your bottle
            measures dissolved solids; it cannot detect these compounds.
          </div>
        </div>
      )}
    </div>
  );
}

export default function CompareMyWatersScreen({ apiBase = "", onClose = () => {}, onUpgrade = null }) {
  const [board, setBoard] = useState(null);
  const [municipal, setMunicipal] = useState(null);
  const [journeyLine, setJourneyLine] = useState("");
  const [tier, setTier] = useState("plus");      // v1.1: server-shaped board tier
  const [lockedCount, setLockedCount] = useState(0);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [openId, setOpenId] = useState(null);

  /* v1.1: keep a row's location chip current after the detail's picker
     saves. Display metadata only; nothing else on the row changes. */
  const setRowLocation = (sourceId, loc) => {
    setBoard((prev) => prev
      ? prev.map((s) => (s.id === sourceId ? { ...s, fillLocation: loc } : s))
      : prev);
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const email = getEmail();
      if (!email) { setLoadState("error"); return; }
      try {
        const params = new URLSearchParams({ email });
        const res = await fetch(`${apiBase}/api/bottle/compare/sources?${params.toString()}`, {
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error(String(res.status));
        const json = await res.json();
        if (cancelled) return;
        setBoard(json.board || []);
        setMunicipal(json.municipalReference || null);
        setJourneyLine(json.journeyLine || "");
        setTier(json.tier === "free" ? "free" : "plus");
        setLockedCount(Number(json.lockedCount) || 0);
        setLoadState("ready");
      } catch (e) {
        console.error("compare board failed:", e.message);
        if (!cancelled) setLoadState("error");
      }
    }
    load();
    return () => { cancelled = true; };
  }, [apiBase]);

  const ranked = board ? board.filter((s) => s.ranked) : [];
  const unranked = board ? board.filter((s) => !s.ranked) : [];
  const podium = (ranked.length > 0 ? ranked : unranked).slice(0, 3);
  /* v1.1: top-rank callout. Provenance wording ONLY: "by your own
     tests" is a claim about whose measurements produced the rank,
     never a physiological claim. Renders only when a source is
     actually ranked #1; nothing is pre-declared. */
  const topRanked = ranked.length > 0 && ranked[0].rank === 1 ? ranked[0] : null;

  const overlay = {
    position: "fixed", inset: 0, zIndex: 70, background: C.white,
    overflowY: "auto", WebkitOverflowScrolling: "touch",
    fontFamily: "'Montserrat', 'Helvetica Neue', sans-serif", color: C.ink,
    padding: "calc(env(safe-area-inset-top) + 14px) 18px 40px",
  };

  return (
    <div style={overlay} role="dialog" aria-label="Water Rankings">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 4 }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.14em",
            textTransform: "uppercase", color: C.gray,
            fontFamily: "'Barlow Condensed', 'Montserrat', sans-serif",
          }}>
            Trust But Verify
          </div>
          <h1 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em" }}>
            Water Rankings
          </h1>
        </div>
        <button onClick={onClose} aria-label="Close"
          style={{ border: "none", background: "transparent", cursor: "pointer", padding: 8 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke={C.gray} strokeWidth="2.5" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
      {journeyLine && (
        <p style={{ margin: "6px 0 18px", fontSize: 12, lineHeight: 1.5, color: C.gray, maxWidth: 420 }}>
          {journeyLine}
        </p>
      )}

      {loadState === "loading" && (
        <div>
          <div style={{ height: 110, background: C.light, borderRadius: 16, marginBottom: 12 }} />
          <div style={{ height: 56, background: C.light, borderRadius: 12, marginBottom: 8 }} />
          <div style={{ height: 56, background: C.light, borderRadius: 12 }} />
        </div>
      )}

      {loadState === "error" && (
        <div style={{ padding: "24px 0", fontSize: 13, color: C.gray, textAlign: "center" }}>
          Could not load your waters. Check your connection and reopen this screen.
        </div>
      )}

      {loadState === "ready" && board.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 12px" }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
            Your scoreboard starts with one test
          </div>
          <p style={{ fontSize: 12.5, color: C.gray, lineHeight: 1.6, maxWidth: 320, margin: "0 auto" }}>
            Run a water test, tag its source, and it appears here. Tap water,
            filtered, bottled brands: each one becomes a data point in your own
            experiment.
          </p>
        </div>
      )}

      {loadState === "ready" && board.length > 0 && (
        <>
          {/* v1.1: top-rank callout, provenance wording only */}
          {topRanked && (
            <div style={{
              background: C.blueSoft, borderRadius: 14, padding: "12px 14px",
              marginBottom: 14, display: "flex", alignItems: "center", gap: 10,
            }}>
              <span style={{ fontSize: 18 }} aria-hidden="true">🏆</span>
              <div style={{ fontSize: 12.5, lineHeight: 1.5, color: C.ink }}>
                <strong>{topRanked.name}</strong> ranks #1 by your own tests
                {topRanked.testCount > 0 ? ` (${topRanked.testCount} test${topRanked.testCount === 1 ? "" : "s"})` : ""}.
              </div>
            </div>
          )}

          {/* Podium strip (plus tier: the free board has one row) */}
          {tier === "plus" && (
          <div style={{
            display: "flex", gap: 10, alignItems: "flex-end",
            padding: "8px 0 18px", overflowX: "auto",
          }}>
            {podium.map((s, i) => (
              <div key={s.id} style={{
                flex: "0 0 auto", width: i === 0 ? 132 : 112,
                background: C.white, border: `1.5px solid ${i === 0 ? C.blue : C.line}`,
                borderRadius: 16, padding: "12px 10px 10px", textAlign: "center",
              }}>
                <ScoreVessel score={s.score} band={s.scoreBand} size={i === 0 ? 64 : 52} vid={s.id} />
                <div style={{
                  fontSize: 12, fontWeight: 800, marginTop: 6,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {s.name}
                </div>
                <div style={{ fontSize: 10, color: C.gray, marginTop: 2 }}>
                  {s.tds != null ? `${Math.round(s.tds)} · ${s.tdsWord}` : "··"}
                </div>
                <div style={{ marginTop: 6 }}>
                  {s.ranked ? (
                    <span style={{
                      fontSize: 10, fontWeight: 800, color: C.blue,
                      letterSpacing: "0.08em",
                    }}>
                      RANK {s.rank}
                    </span>
                  ) : (
                    <ProgressDots tests={s.progress.tests} needed={s.progress.needed} />
                  )}
                </div>
              </div>
            ))}
          </div>
          )}

          {/* The board */}
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
            textTransform: "uppercase", color: C.gray, margin: "0 0 8px",
            fontFamily: "'Barlow Condensed', 'Montserrat', sans-serif",
          }}>
            All sources
          </div>
          {board.map((s) => (
            <div key={s.id}
              style={{
                border: `1px solid ${C.line}`, borderRadius: 14,
                padding: "12px 14px", marginBottom: 8, background: C.white,
              }}>
              <div
                onClick={() => setOpenId(openId === s.id ? null : s.id)}
                style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
              >
                <div style={{
                  width: 26, textAlign: "center", fontSize: 13, fontWeight: 800,
                  color: s.ranked ? C.ink : C.gray,
                }}>
                  {s.ranked ? s.rank : "·"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{
                      fontSize: 13.5, fontWeight: 700,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {s.name}
                    </span>
                    <EvidenceBadge evidence={s.evidence} />
                    <LocationChip location={s.fillLocation} />
                  </div>
                  <div style={{ fontSize: 11, color: C.gray, marginTop: 3 }}>
                    {s.tds != null ? `${Math.round(s.tds)} · ${s.tdsWord}` : "No scored tests yet"}
                    {s.testCount > 0 ? ` · ${s.testCount} test${s.testCount === 1 ? "" : "s"}` : ""}
                  </div>
                  {!s.ranked && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5 }}>
                      <ProgressDots tests={s.progress.tests} needed={s.progress.needed} />
                      <span style={{ fontSize: 10, color: C.gray }}>
                        Keep testing to rank this water
                      </span>
                    </div>
                  )}
                </div>
                <Sparkline points={s.sparkline} band={s.scoreBand} />
                <div style={{
                  fontSize: 17, fontWeight: 800, width: 34, textAlign: "right",
                  color: bandColor(s.scoreBand),
                }}>
                  {s.score == null ? "··" : Math.round(s.score)}
                </div>
              </div>
              {openId === s.id && (
                <SourceDetail
                  apiBase={apiBase}
                  sourceId={s.id}
                  onLocationChange={setRowLocation}
                  onUpgrade={onUpgrade}
                />
              )}
            </div>
          ))}

          {/* v1.1: free-tier locked rows. The server sent a COUNT, not
              the sources: these rows honestly show that locked waters
              exist without any data behind them. */}
          {tier === "free" && lockedCount > 0 && (
            <>
              {Array.from({ length: Math.min(lockedCount, 4) }).map((_, i) => (
                <div key={`locked-${i}`} style={{
                  border: `1px dashed ${C.line}`, borderRadius: 14,
                  padding: "12px 14px", marginBottom: 8, background: C.light,
                  display: "flex", alignItems: "center", gap: 12, opacity: 0.8,
                }}>
                  <span style={{ fontSize: 14 }} aria-hidden="true">🔒</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ width: "55%", height: 10, borderRadius: 6, background: "rgba(166,168,171,0.35)" }} />
                    <div style={{ width: "35%", height: 8, borderRadius: 6, background: "rgba(166,168,171,0.22)", marginTop: 6 }} />
                  </div>
                </div>
              ))}
              <div style={{
                background: C.blueSoft, borderRadius: 14, padding: "12px 14px",
                marginTop: 4, fontSize: 12, lineHeight: 1.6, color: C.ink,
              }}>
                {lockedCount === 1
                  ? "1 more of your waters is ranked in WTR+."
                  : `${lockedCount} more of your waters are ranked in WTR+.`}
                {" "}Your full rankings, source detail, and Sip Intelligence
                unlock with WTR+.
                {onUpgrade && (
                  <button onClick={onUpgrade} style={{
                    display: "block", marginTop: 8,
                    border: "none", background: C.blue, color: C.white,
                    borderRadius: 999, padding: "8px 18px", fontSize: 11.5,
                    fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                    letterSpacing: "0.03em",
                  }}>
                    UPGRADE TO WTR+
                  </button>
                )}
              </div>
            </>
          )}

          {/* Municipal reference, quiet, never ranked */}
          {municipal && (
            <div style={{
              background: C.light, borderRadius: 14, padding: "12px 14px", marginTop: 14,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{municipal.name}</span>
                <EvidenceBadge evidence="municipal_record" />
              </div>
              <div style={{ fontSize: 11, color: C.gray, marginTop: 4, lineHeight: 1.5 }}>
                Public record for your utility, confidence {municipal.tier}. A record
                is not a reading: your faucet can differ from the system average.
              </div>
              <div style={{
                marginTop: 8, fontSize: 11.5, fontWeight: 700, color: C.blue,
              }}>
                {municipal.cta}
              </div>
            </div>
          )}

          <p style={{ fontSize: 10.5, color: C.gray, lineHeight: 1.6, marginTop: 18 }}>
            Rankings use your measured tests only. Sources rank after 3 scored
            tests. Published label and municipal data appear for reference and
            never compete with your measurements.
          </p>
        </>
      )}
    </div>
  );
}
